import React, { Component } from "react";
import { BASE_URL } from "../../static/constants";
import getToken from "../../utils/getToken";
import formatDate from "../../utils/formatDate";

import {
  enrollPatient,
  disablePatient,
  enablePatient,
} from "../../actions/patients";

import Table from "./Table";
import { connect } from "react-redux";
import LoadingSpinner from "../../components/LoadingSpinner";
import { showErrorToast, showSuccessToast, showToast } from "utils/toast";
import { setTitle } from "app/store/transmission/slice/transmissionSlice";

class Patients extends Component {
  constructor(props) {
    super(props);
    this.state = {
      patientData: [],
      physicianData: [],
      fullPatientData: [],
      dataLoaded: false,
      isFilters: false,
      transmissions: {
        active: false,
        late: false,
      },
    };

    this.getPatients = this.getPatients.bind(this);
    this.enrollPatientSubmit = this.enrollPatientSubmit.bind(this);
    this.disablePatientSubmit = this.disablePatientSubmit.bind(this);
    this.enablePatientSubmit = this.enablePatientSubmit.bind(this);
    this.changeRpmProps = this.changeRpmProps.bind(this);
    this.changeFilter = this.changeFilter.bind(this);
  }

  componentDidMount() {
    this.getPatients();
    this.getPhysicians();
  }

  componentDidUpdate(prevProps) {
    let { patientData } = this.props;
    if (prevProps.patientData !== patientData) {
      if (patientData.patientEnrollData) {
        const { error, message } = patientData.patientEnrollData;
        if (error) {
          showErrorToast(message);
        } else {
          showSuccessToast(message);
          this.getPatients();
          this.getPhysicians();
        }
      }
    }
    this.props.setTitle({
      title: "RPM Patients",
    });
  }

  getPatients() {
    const { role, setActiveView } = this.props;
    this.setState({
      dataLoaded: false,
    });
    fetch(`${BASE_URL}/v2/rpm`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${getToken()}`,
      },
    })
      .then((response) => {
        return response.json();
      })
      .then((data) => {
        if (data.error) {
          throw Error(data.message);
        }
        this.setState({
          dataLoaded: true,
        });

        const patientData = data.patients.map((patient) => {
          const obj = {
            id: patient._id,
            account: patient.account,
            manufacturer: patient.manufacturer,
            dateOfBirth: formatDate(patient.dob, "MM/DD/YYYY"),
            device: patient.device,
            email: null,
            name: patient.name,
            role: null,
            serialNumber: patient.serial_number,
            active: patient.active || false,
            scheduled: patient.scheduled,
            lastTransmittedDate: patient?.lastTransmittedDate,
            bpReadingViewedByMe: patient?.bpReadingViewedByMe,
            firstBPReadingHistory: patient?.firstBPReadingHistory,
            lastSignalStrength: patient?.lastSignalStrength,
          };

          return obj;
        });

        const fullPatientData = [...patientData];

        this.setState({
          patientData,
          fullPatientData,
        });

        setActiveView(role);
      })
      .catch((error) => {
        const errorStr = error.toString();
        showToast({ message: errorStr, type: "error" });
        if (errorStr === "Error: jwt malformed") {
          this.props.navigate("/login");
        }
        if (errorStr === "Error: jwt expired") {
          this.props.navigate("/login");
        }
        if (errorStr === "Error: Unauthorized") {
          this.props.navigate("/prohibited");
        }
      });
  }

  getPhysicians() {
    fetch(`${BASE_URL}/patient/physicians`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${getToken()}`,
      },
    })
      .then((response) => {
        return response.json();
      })
      .then((data) => {
        if (data.message) {
          throw Error(data.message);
        }

        const physicianData = data.physicians.map((physician) => {
          const obj = {
            name: physician.name,
            accounts: physician.accounts,
            userId: physician.userId,
          };

          return obj;
        });

        this.setState({
          physicianData,
        });
      })
      .catch((error) => {
        const errorStr = error.toString();
        if (errorStr === "Error: jwt malformed") {
          this.props.navigate("/login");
        }
        if (errorStr === "Error: jwt expired") {
          this.props.navigate("/login");
        }
        if (errorStr === "Error: Unauthorized") {
          this.props.navigate("/prohibited");
        }

        showErrorToast(errorStr);
      });
  }

  enrollPatientSubmit(payload) {
    return this.props.enrollPatient(payload);
  }

  async disablePatientSubmit(serialNumber) {
    const { patientData } = this.state;
    await this.props.disablePatient(serialNumber).then((response) => {
      const { data } = response;
      if (!data.error) {
        // needs to be moved to redux
        const patientDataCopy = [...patientData];
        const match = patientDataCopy.find((patient) => {
          return patient.serialNumber === serialNumber;
        });
        match.active = false;
        this.setState({
          patientData: patientDataCopy,
        });
        showSuccessToast(data.message);
      }
    });
  }

  async enablePatientSubmit(serialNumber) {
    const { patientData } = this.state;
    await this.props.enablePatient(serialNumber).then((response) => {
      const { data } = response;
      if (!data.error) {
        // needs to be moved to redux
        const patientDataCopy = [...patientData];
        const match = patientDataCopy.find((patient) => {
          return patient.serialNumber === serialNumber;
        });
        match.active = true;
        this.setState({
          patientData: patientDataCopy,
        });
        showSuccessToast(data.message);
      }
    });
  }

  changeFilter(filters) {
    this.setState({
      isFilters: filters,
    });
  }

  changeRpmProps(logic, active = false, late = false) {
    let dataToFilter = [];
    function getOptionsLogic(patient) {
      return {
        active: patient?.scheduled === true,
        late: patient?.scheduled === false,
      };
    }

    if (!(active || late)) {
      this.setState({
        patientData: this.state.fullPatientData,
      });
      return;
    }

    if (logic === "and") {
      dataToFilter = [...this.state.patientData].filter((patient) => {
        if (active && late) {
          const logics = getOptionsLogic(patient);
          if (logics["active"] && logics["late"]) {
            return patient;
          }
        } else if (active || late) {
          const options = { active, late };
          let option;
          for (let [key, value] of Object.entries(options)) {
            if (value) {
              option = key;
              break;
            }
          }
          if (getOptionsLogic(patient)[option]) {
            return patient;
          }
        }

        return null;
      });

      this.setState({
        patientData: dataToFilter,
      });
      return;
    } else if (logic === "or") {
      dataToFilter = [...this.state.fullPatientData].filter((patient) => {
        if (active && late) {
          const logics = getOptionsLogic(patient);
          if (logics["active"] || logics["late"]) {
            return patient;
          }
        } else if (active || late) {
          const options = { active, late };
          let option;
          for (let [key, value] of Object.entries(options)) {
            if (value) {
              option = key;
              break;
            }
          }
          if (getOptionsLogic(patient)[option]) {
            return patient;
          }
        }

        return null;
      });
      this.setState({
        patientData: dataToFilter,
      });
      return;
    }
  }

  render() {
    const { patientData, physicianData, dataLoaded, transmissions, isFilters } =
      this.state;
    const { role, patient, navigate } = this.props;
    const patientProps = {
      patientData,
      transmissions,
      isFilters,
      patient,
    };

    return (
      <div>
        {!dataLoaded && (
          <div style={{ padding: "50px 0", textAlign: "center" }}>
            <LoadingSpinner />
          </div>
        )}
        {dataLoaded && (
          <Table
            patientProps={patientProps}
            physicianData={physicianData}
            getPatients={this.getPatients}
            history={this.props.history}
            role={role}
            enrollPatientSubmit={this.enrollPatientSubmit}
            disablePatientSubmit={this.disablePatientSubmit}
            enablePatientSubmit={this.enablePatientSubmit}
            changeRpmProps={this.changeRpmProps}
            changeFilter={this.changeFilter}
            navigate={navigate}
          />
        )}
      </div>
    );
  }
}

const mapStateToProps = (state) => {
  return {
    patientData: state.patients,
    patient: state.patient,
  };
};

export default connect(mapStateToProps, {
  enrollPatient,
  disablePatient,
  enablePatient,
  setTitle,
})(Patients);
